import { Link, useParams } from "react-router-dom";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";
import httpService from "../../../services/http/products/product.service";
import { useEffect, useState } from "react";
import UnlimitLoading from "../../../components/ui-kit/unlimit-loading";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import usePagination, {
  createInitialPagination,
} from "../../../custom-hooks/usePagination";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { useTranslation } from "react-i18next";
import ThreedFileWindow from "../../sample/sample-iphone/3d-window";
import UnlimitImageGallery from "../../../components/ui-kit/unlimit-image-gallery";
import UnlimitFieldDetailsRow from "../../../components/ui-kit/unlimit-field-details-row";
import { useForm } from "react-hook-form";
import moment from "moment";
import useRoleAndPerm from "../../../utils/permissionFunction";
import ProductSalePriceDialog from "./product-sale-price-dialog";
import formatNumber from "../../../utils/numberFunction";
import { useSelectedCountry } from "../../../feature-module/dashboard/hooks/useSelectedCountry";
import salesService from "../../../services/http/sales/sales.service";

export default function ProductDetails() {
  const { t } = useTranslation();
  const { register } = useForm();
  const initialPagination = createInitialPagination(t);

  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);
  const [loading, setLoading] = useState(false);

  const { hasPermission } = useRoleAndPerm();
  const [pageLoading, setPageLoading] = useState(false);
  const selectedCountry = useSelectedCountry();

  const { id } = useParams();
  const [product, setProduct] = useState(null);

  const [salesOrderHistory, setSaleOrderHistory] = useState([]);

  const getProduct = () => {
    setPageLoading(true);
    httpService
      .getProduct(id)
      .then((response) => {
        if (response && response.status === 200) {
          setProduct(response.data.data);
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setPageLoading(false);
      });
  };

  const getSalesOrderHistory = async (
    pagination = paginationParams.pagination
  ) => {
    const data = {
      page: pagination?.current ?? 1,
      per_page: pagination?.pageSize ?? 10,
      product_id: id,
    };

    setLoading(true);

    const response = await salesService
      .salesList(data)
      .then((response) => {
        if (response.status == 200) {
          setSaleOrderHistory(response.data.data?.data);
        }

        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (!pagination) {
      pagination = initialPagination;
    }
    await updatePaginationParams({
      pagination,
      sorter,
    });
    await getSalesOrderHistory(pagination);
  };

  useEffect(() => {
    if (id) {
      getProduct();
      getSalesOrderHistory();
    }
  }, [id]);

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      align: "center",
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: t("Sales.SalesID"),
      dataIndex: "order_code",
      render: (text, record) => {
        return record?.order_code ?? "";
      },
    },
    {
      title: t("inputField.label.lmUserID"),
      dataIndex: "lm_user_id",
      render: (text, record) => {
        return record?.shopper?.lm_shopper_number ?? "";
      },
    },
    {
      title: t("inputField.label.shopperName"),
      dataIndex: "shopper_name",
      render: (text, record) => {
        return record?.shopper?.name ?? "";
      },
    },
    {
      title: t("tableColumnName.lmAccountNo"),
      dataIndex: "lm_account_no",
      render: (text, record) => {
        return record?.company_account?.lm_account_number ?? "";
      },
    },
    {
      title: t("Sales.Reference"),
      dataIndex: "reference",
    },
    {
      title: t("logs.date"),
      dataIndex: "created_at",
      render: (text) => {
        return text ? moment(text).format("DD MMM YYYY - HH:mm:ss") : "";
      },
    },
    {
      title: t("shop.shop"),
      dataIndex: "outlet",
      hidden: false,
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <ImageWithBasePath
            type="link"
            src={record.outlet?.image_url}
            alt="Contact"
            style={{ width: 30, height: 30, marginRight: 10 }}
          />
          {record?.outlet?.name}
        </div>
      ),
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      hidden: !hasPermission(["sale-view"]),
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {hasPermission(["sale-view"]) && (
              <Link
                to={`/sales/${record.id}/details`}
                className="me-2 edit-icon p-2"
              >
                <i className="feather-eye"></i>
              </Link>
            )}
          </div>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("product.product"), link: "/product-list" },
    { label: t("product.productDetails"), active: true },
  ];

  const onClickEditPrice = () => {
    const editPrice = document.querySelector(".btn-edit-price");
    if (editPrice) {
      editPrice.click();
    }
  };

  const onHideModal = async () => {
    await getProduct();
  };

  const calculateProfit = (salePrice, costPrice) => {
    const profitAmount = salePrice - costPrice;
    const profitPercentage = costPrice ? (profitAmount / costPrice) * 100 : 0;
    return {
      profitAmount: profitAmount.toFixed(2),
      profitPercentage: profitPercentage.toFixed(2),
    };
  };

  const salePrice = parseFloat(product?.price_line?.sale_price || 0);
  const costPrice = parseFloat(product?.cost_price || 0);

  const { profitAmount, profitPercentage } = calculateProfit(
    salePrice,
    costPrice
  );
  const data = [
    { field: t("inputField.label.EAN"), data: product?.ean },
    {
      field: t("tableColumnName.lmProductNo"),
      data: product?.lm_product_number,
    },
    { field: t("brand.brand"), data: product?.brand?.name },
    {
      field: t("inputField.label.productCategory"),
      data: product?.category?.name,
    },

    {
      field: t("inputField.label.productInformation"),
      data: product?.product_information,
    },
    { field: t("inputField.label.weight"), data: product?.net_weight },
    {
      field: t("inputField.label.actualWeightByZemicSensor"),
      data: product?.actual_zemic_sensor_weight,
    },
    //{ field: t("inputField.label.weightBuffer"), data: product?.weight_buffer },
    { field: t("inputField.label.uomCode"), data: product?.uom_code_1?.code },
    {
      field: t("inputField.label.uomValue"),
      data: product?.uom_code_1_value,
    },
    {
      field: t("inputField.label.receiptLabelText"),
      data: product?.receipt_label_text,
    },
    {
      field: t("inputField.label.shortDescription"),
      data: product?.short_description,
    },
    { field: t("tableColumnName.sku"), data: product?.sku },
    //   { field: t("inputField.label.uomCode2"), data: product?.uom_code_2?.name },
    //   { field: t("inputField.label.uomCode2Value"), data: product?.uom_code_2_value },
    {
      field: t("inputField.label.securityableProduct"),
      data: product?.is_security_product ? "Yes" : "No",
    },
    {
      field: t("inputField.label.extraSecurityCheck"),
      data: product?.extra_security_check ? "Yes" : "No",
    },
    {
      field: t("inputField.label.compoundProduct"),
      data: product?.is_compound_product ? "Yes" : "No",
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <Link
          style={{ visibility: "hidden" }}
          to="#"
          data-bs-toggle="modal"
          data-bs-target="#edit-product-price"
          className="btn-edit-price"
        ></Link>
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h3 className="text-black">{t("product.productDetails")}</h3>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>
          </div>
          {pageLoading ? (
            <div className="row d-flex justify-content-center">
              <UnlimitLoading width={50} height={50} />
            </div>
          ) : (
            product && (
              <>
                <div className="row">
                  <div className="col-md-9">
                    <div className="card container-fluid">
                      <div className="d-flex my-3 mx-2">
                        <h3 className="me-3">{product?.name}</h3>
                        <span
                          className={`badge ${
                            product?.is_active == 1
                              ? "badge-linesuccess"
                              : "badge-danger"
                          }`}
                        >
                          <a>
                            {product?.is_active == 1 ? "Active" : "Deactive"}
                          </a>
                        </span>
                      </div>
                      {data.map(({ field, data, update, onClick, classes }) => (
                        <UnlimitFieldDetailsRow
                          key={field}
                          heading={field}
                          update={update}
                          details={data}
                          onClick={() => onClick()}
                          classes={classes}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card">
                      <UnlimitImageGallery
                        urls={{
                          file: product?.main_product_image,
                          is_active: 1,
                        }}
                        isDefaultValue={product?.main_product_image}
                        register={register}
                        disabled={true}
                        totalLength={product?.product_images?.length}
                        navigateTo={`/product/${product?.id}/image-details`}
                      />
                    </div>
                    <div className="card">
                      {Object.keys(product?.three_d_files || {}).length >=
                        3 && (
                        <ThreedFileWindow
                          height={"400px"}
                          id={id}
                          files={product?.three_d_files}
                          navigateBack={"/product/:id/object-upload"}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            )
          )}

          <UnlimitTable
            pagination={paginationParams.pagination}
            columns={columns}
            dataSource={salesOrderHistory}
            rowKey={(record) => record.id}
            loading={loading}
            onChange={handleTableChange}
          />
        </div>
        <ProductSalePriceDialog
          onHide={onHideModal}
          salePrice={product?.price_line?.sale_price ?? 0}
          id={product?.price_line?.id}
        />
      </div>
    </>
  );
}
